import type { ReactNode } from 'react'
import { useState } from 'react'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Award, KeyRound, Lock, LogIn, Plus, Wallet } from 'lucide-react'
import { api, errorMessage } from '@/lib/api'
import { cn } from '@/lib/cn'
import { formatDate, formatDateTime } from '@/lib/date'
import { money } from '@/lib/money'
import { useAuth } from '@/context/AuthContext'
import type { CustomerRow, CustomerWallet, Paginated } from '@/types'
import AdjustWalletModal from '@/components/customer/AdjustWalletModal'
import SetTierModal from '@/components/customer/SetTierModal'
import SetPasswordModal from '@/components/admin/SetPasswordModal'
import PageHeader from '@/components/ui/PageHeader'
import Button from '@/components/ui/Button'
import ConfirmDialog from '@/components/ui/ConfirmDialog'
import Drawer from '@/components/ui/Drawer'
import Modal from '@/components/ui/Modal'
import Pager from '@/components/ui/Pager'
import { Field, Input } from '@/components/ui/Field'
import Badge, { StateBadge } from '@/components/ui/Badge'
import TierBadge from '@/components/ui/TierBadge'
import { Table, Row, Cell, EmptyRow } from '@/components/ui/Table'
import { useToast } from '@/components/ui/toast'
import LogoLoader from '@/components/ui/LogoLoader'

interface CustomerDraft {
  name: string
  phone: string
  company_name: string
}

const emptyDraft: CustomerDraft = { name: '', phone: '', company_name: '' }

/**
 * The customers list and detail drawer, shared by the admin and sales
 * consoles. Suspending, the wallet statement with its hand adjustments and
 * "Sign in as", setting the tier by hand and setting a password are admin only and passed in rather than inferred from the
 * role, so a control added here later cannot leak into sales by default.
 * Those routes are absent from the sales route group, so the server refuses
 * them regardless.
 */
export default function CustomersPage({
  basePath,
  canSuspend,
  canAdjustWallet,
  canSignInAs,
  canSetTier,
  canSetPassword,
}: {
  basePath: '/admin' | '/sales'
  canSuspend: boolean
  canAdjustWallet: boolean
  canSignInAs: boolean
  canSetTier: boolean
  canSetPassword: boolean
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const { impersonate } = useAuth()
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [draft, setDraft] = useState<CustomerDraft | null>(null)
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [adjusting, setAdjusting] = useState(false)
  const [settingTier, setSettingTier] = useState(false)
  const [confirmSignIn, setConfirmSignIn] = useState<CustomerRow | null>(null)
  const [passwordFor, setPasswordFor] = useState<CustomerRow | null>(null)

  const wallet = useQuery({
    queryKey: ['customer-wallet', selectedId],
    queryFn: async () => (await api.get<CustomerWallet>(`/admin/customers/${selectedId}/wallet`)).data,
    enabled: canAdjustWallet && selectedId !== null,
  })

  const { data, isLoading } = useQuery({
    queryKey: ['customers-page', basePath, search, page],
    queryFn: async () =>
      (
        await api.get<Paginated<CustomerRow>>(`${basePath}/customers`, {
          params: { page, ...(search ? { search } : {}) },
        })
      ).data,
    // Every keystroke is a new query. Without this it starts with no data,
    // the full-page loader below replaces the search box mid-word, and the
    // typing is lost. The last list stays on screen until the new one lands.
    placeholderData: keepPreviousData,
  })

  const update = useMutation({
    mutationFn: async ({ id, status }: { id: number; status: string }) =>
      (await api.patch(`/admin/customers/${id}`, { status })).data,
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['customers-page'] })
      toast.success(res.message ?? 'Customer updated')
    },
    onError: (err) => toast.error('Update failed', errorMessage(err)),
  })

  const create = useMutation({
    mutationFn: async (d: CustomerDraft) =>
      (
        await api.post(`${basePath}/customers`, {
          name: d.name,
          phone: d.phone,
          company_name: d.company_name || null,
        })
      ).data,
    onSuccess: (res: { message?: string }) => {
      queryClient.invalidateQueries({ queryKey: ['customers-page'] })
      setDraft(null)
      toast.success(res.message ?? 'Customer added')
    },
    onError: (err) => toast.error('Could not add the customer', errorMessage(err)),
  })

  const signIn = useMutation({
    // The context swaps the user and moves to the customer's account together.
    mutationFn: async (customer: CustomerRow) => impersonate({ id: customer.id, kind: 'customer' }),
    onSuccess: () => setConfirmSignIn(null),
    onError: (err) => {
      setConfirmSignIn(null)
      toast.error('Could not sign in as them', errorMessage(err))
    },
  })

  if (isLoading && !data) return <LogoLoader />

  // Derived from the live query so a suspend/reactivate re-renders the drawer.
  const selected = data?.data.find((customer) => customer.id === selectedId) ?? null

  return (
    <div>
      <PageHeader
        title="Customers"
        subtitle="Registered through the system or added manually as walk-ins"
        actions={
          <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
            <Input
              placeholder="Search name, email, phone..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value)
                setPage(1)
              }}
              className="w-full sm:w-64"
            />
            <Button onClick={() => setDraft(emptyDraft)}>
              <Plus className="size-4" /> Add customer
            </Button>
          </div>
        }
      />

      <Table columns={['Customer', 'Tier', 'Orders', 'Total spent', 'Wallet', 'Lifetime top-up', 'Joined']}>
        {(data?.data.length ?? 0) === 0 && <EmptyRow colSpan={7} />}
        {data?.data.map((customer) => (
          <Row key={customer.id} onClick={() => setSelectedId(customer.id)}>
            <Cell>
              <p className="font-semibold">{customer.name}</p>
              <p className="text-xs text-muted">{customer.company_name ?? customer.email ?? customer.phone}</p>
            </Cell>
            <Cell>
              {customer.tier ? (
                <span className="inline-flex items-center gap-1">
                  <TierBadge name={customer.tier.name} color={customer.tier.color} icon={customer.tier.icon} />
                  {customer.tier_lock_active && <Lock className="size-3.5 text-muted" aria-label="Tier locked" />}
                </span>
              ) : (
                <span className="text-muted">-</span>
              )}
            </Cell>
            <Cell className="font-semibold">{customer.orders_count}</Cell>
            <Cell className="font-semibold">{money(customer.total_spent)}</Cell>
            <Cell>{money(customer.wallet_balance)}</Cell>
            <Cell className="text-xs">{money(customer.lifetime_topup)}</Cell>
            <Cell className="text-xs">{formatDate(customer.joined_at)}</Cell>
          </Row>
        ))}
      </Table>
      <Pager meta={data?.meta} page={page} onPage={setPage} />

      <Drawer
        open={Boolean(selected)}
        // Escape reaches both listeners; the open dialog takes it, not the drawer under it.
        onClose={() => !adjusting && !settingTier && !confirmSignIn && !passwordFor && setSelectedId(null)}
        title="Customer details"
        footer={
          selected &&
          (canSuspend || canSignInAs || canSetPassword) && (
            <div className="flex w-full flex-wrap items-center justify-end gap-2">
              {/* A suspended customer has no account to show; the server refuses it as well. */}
              {canSignInAs && selected.status === 'approved' && (
                <Button variant="outline" className="mr-auto" onClick={() => setConfirmSignIn(selected)}>
                  <LogIn className="size-4" /> Sign in as
                </Button>
              )}
              {/* A walk-in has no email, so no login to set a password for. */}
              {canSetPassword && selected.email && (
                <Button variant="outline" className={canSignInAs ? undefined : 'mr-auto'} onClick={() => setPasswordFor(selected)}>
                  <KeyRound className="size-4" /> Set password
                </Button>
              )}
              {canSuspend &&
                (selected.status === 'approved' ? (
                  <Button
                    variant="danger"
                    loading={update.isPending}
                    onClick={() => update.mutate({ id: selected.id, status: 'suspended' })}
                  >
                    Suspend
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    loading={update.isPending}
                    onClick={() => update.mutate({ id: selected.id, status: 'approved' })}
                  >
                    Reactivate
                  </Button>
                ))}
            </div>
          )
        }
      >
        {selected && (
          <div className="space-y-5">
            <div>
              <div className="flex items-center justify-between gap-2">
                <h4 className="text-lg font-bold">{selected.name}</h4>
                <StateBadge state={selected.status} />
              </div>
              {(selected.tier || canSetTier) && (
                <div className="mt-1.5 flex flex-wrap items-center gap-2">
                  {selected.tier && <TierBadge name={selected.tier.name} color={selected.tier.color} icon={selected.tier.icon} />}
                  {selected.tier_lock_active && (
                    <Badge tone="warning">
                      <Lock className="size-3" />
                      {selected.tier_lock_expires_at ? `Locked until ${formatDate(selected.tier_lock_expires_at)}` : 'Locked'}
                    </Badge>
                  )}
                  {canSetTier && (
                    <Button variant="outline" className="ml-auto" onClick={() => setSettingTier(true)}>
                      <Award className="size-4" /> Set tier
                    </Button>
                  )}
                </div>
              )}
              {selected.tier_set_by_name && (
                <p className="mt-1 text-2xs text-muted">
                  Tier set by {selected.tier_set_by_name} on {formatDate(selected.tier_set_at)}
                  {!selected.tier_lock_active && ' - top-ups decide it again'}
                </p>
              )}
            </div>

            <div className="space-y-2 text-sm">
              <DetailRow label="Email">
                {selected.email ?? <span className="text-muted">No account - walk-in customer</span>}
              </DetailRow>
              <DetailRow label="Phone">{selected.phone ?? '-'}</DetailRow>
              <DetailRow label="Company">{selected.company_name ?? '-'}</DetailRow>
              <DetailRow label="Joined">{formatDate(selected.joined_at)}</DetailRow>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <StatBox label="Orders" value={String(selected.orders_count)} />
              <StatBox label="Total spent" value={money(selected.total_spent)} />
              <StatBox label="Wallet" value={money(selected.wallet_balance)} />
              <StatBox label="Lifetime top-up" value={money(selected.lifetime_topup)} />
            </div>

            {canAdjustWallet && (
              <div>
                <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                  <h5 className="text-xs font-semibold uppercase text-muted">Wallet statement</h5>
                  <Button variant="outline" disabled={!wallet.data} onClick={() => setAdjusting(true)}>
                    <Wallet className="size-4" /> Adjust balance
                  </Button>
                </div>
                {wallet.isLoading ? (
                  <p className="text-sm text-muted">Loading...</p>
                ) : wallet.isError ? (
                  <p className="text-sm text-danger">Could not load the wallet statement.</p>
                ) : wallet.data?.transactions.length === 0 ? (
                  <p className="rounded-lg border border-line p-3 text-center text-sm text-muted">No transactions yet.</p>
                ) : (
                  <ul className="divide-y divide-line rounded-lg border border-line">
                    {wallet.data?.transactions.map((tx) => (
                      <li key={tx.id} className="flex items-start justify-between gap-3 px-3 py-2 text-sm">
                        <div className="min-w-0">
                          <p className="break-words font-medium">{tx.description}</p>
                          <p className="text-2xs text-muted">
                            {formatDateTime(tx.at)}
                            {tx.by && ` - by ${tx.by}`}
                          </p>
                        </div>
                        <div className="shrink-0 text-right">
                          <p className={cn('font-bold', tx.type === 'credit' ? 'text-status-success' : 'text-status-danger')}>
                            {tx.type === 'credit' ? '+' : '-'}
                            {money(tx.amount)}
                          </p>
                          <p className="text-2xs text-muted">Balance {money(tx.balance_after)}</p>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        )}
      </Drawer>

      <AdjustWalletModal
        customer={adjusting ? selected : null}
        balance={wallet.data?.balance ?? selected?.wallet_balance ?? 0}
        onClose={() => setAdjusting(false)}
      />

      <SetTierModal customer={settingTier ? selected : null} onClose={() => setSettingTier(false)} />

      <SetPasswordModal
        account={
          passwordFor?.email
            ? { name: passwordFor.name, email: passwordFor.email, endpoint: `/admin/customers/${passwordFor.id}/password` }
            : null
        }
        onClose={() => setPasswordFor(null)}
      />

      <ConfirmDialog
        open={!!confirmSignIn}
        onClose={() => setConfirmSignIn(null)}
        onConfirm={() => confirmSignIn && signIn.mutate(confirmSignIn)}
        title={confirmSignIn ? `Sign in as ${confirmSignIn.name}?` : ''}
        confirmLabel="Sign in as them"
        loading={signIn.isPending}
      >
        You will see the shop and their account exactly as they do, for up to two hours. A banner stays on screen
        with a button to come back here. Anything you do there, including placing an order or topping up, is
        recorded under their name.
      </ConfirmDialog>

      <Modal
        open={Boolean(draft)}
        onClose={() => setDraft(null)}
        title="Add customer"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setDraft(null)}>
              Cancel
            </Button>
            <Button
              loading={create.isPending}
              disabled={!draft?.name.trim() || !draft?.phone.trim()}
              onClick={() => draft && create.mutate(draft)}
            >
              Add customer
            </Button>
          </div>
        }
      >
        {draft && (
          <div className="space-y-4">
            <Field label="Full name" htmlFor="customer-name">
              <Input id="customer-name" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
            </Field>
            <Field label="Phone" htmlFor="customer-phone">
              <Input id="customer-phone" value={draft.phone} onChange={(e) => setDraft({ ...draft, phone: e.target.value })} />
            </Field>
            <Field label="Company (optional)" htmlFor="customer-company">
              <Input
                id="customer-company"
                value={draft.company_name}
                onChange={(e) => setDraft({ ...draft, company_name: e.target.value })}
              />
            </Field>
            <p className="text-xs text-foreground/60">
              Walk-in customer without an account: they cannot sign in, but orders can be created for them from
              here. Name and phone are enough.
            </p>
          </div>
        )}
      </Modal>
    </div>
  )
}

function DetailRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3 border-b border-line pb-2">
      <span className="shrink-0 text-xs font-semibold uppercase text-muted">{label}</span>
      <span className="text-right">{children}</span>
    </div>
  )
}

function StatBox({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-line bg-surface p-3">
      <p className="text-xs text-muted">{label}</p>
      <p className="mt-0.5 font-bold">{value}</p>
    </div>
  )
}
